import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { AlertTriangle, ImagePlus, Loader2, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  ACCEPT_IMAGES,
  COVER_ASPECT,
  COVER_WIDTH_PX,
  imageFilesFrom,
} from '@/components/blog/draftImages'
import type { DraftImage } from '@/services/blogApi'

/** Warnings about how the picked file will actually render, not about the file itself. */
function notesFor(image: DraftImage): { text: string; warn: boolean }[] {
  const size = image.width ? `${image.width}×${image.height}` : 'unknown size'
  const notes: { text: string; warn: boolean }[] = [
    { text: `${image.filename} · ${size} · ${Math.round(image.bytes / 1024)}KB`, warn: false },
  ]

  if (image.lowRes) {
    notes.push({
      text: `Narrower than ${COVER_WIDTH_PX}px, so it will look soft on a high-resolution screen. A wider version will fix it.`,
      warn: true,
    })
  }

  // The published cover is cropped to 16:9 with `object-cover`, so anything far
  // off that ratio loses its edges — worth saying before it is on the site.
  // An SVG reports no height and scales anyway, so it is left alone.
  const ratio = image.width && image.height ? image.width / image.height : COVER_ASPECT
  if (Math.abs(ratio - COVER_ASPECT) / COVER_ASPECT > 0.12) {
    notes.push({
      text: 'Not 16:9, so the top and bottom (or the sides) will be cropped off. The preview above shows the crop.',
      warn: true,
    })
  }

  return notes
}

/**
 * The post's cover art.
 *
 * Uploading here is the only safe way to set one: the website `import`s cover
 * files at build time, so a post naming a cover that is not in the repo does not
 * render a broken image — it fails the production build. The upload and the
 * frontmatter name are therefore set together, and never separately.
 */
export default function CoverImage({
  image,
  committedUri,
  filename,
  alt,
  status,
  onPick,
  onAlt,
  onClear,
  disabled = false,
}: {
  /** A file picked in this session, not committed yet. */
  image: DraftImage | null
  /** Data URI of the cover already in the repo, when editing a post that has one. */
  committedUri: string | null
  filename?: string
  alt: string
  status: 'idle' | 'loading' | 'missing'
  onPick: (file: File) => void
  onAlt: (alt: string) => void
  onClear: () => void
  disabled?: boolean
}) {
  const input = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  const preview = image?.dataUri ?? committedUri
  const has = Boolean(preview || filename)

  // Always prevented: an unhandled file drop makes the browser navigate to the
  // file, which would abandon the draft.
  const drop = (event: React.DragEvent) => {
    event.preventDefault()
    setDragging(false)
    if (disabled) return
    const [file] = imageFilesFrom(event.dataTransfer)
    if (file) onPick(file)
    else toast.error('A cover has to be an image.')
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold">Cover image</h2>
        {has && (
          <button
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-600"
            onClick={onClear}
            disabled={disabled}
          >
            <Trash2 className="h-3.5 w-3.5" /> Remove
          </button>
        )}
      </div>

      <input
        ref={input}
        type="file"
        accept={ACCEPT_IMAGES}
        className="hidden"
        onChange={event => {
          const file = event.target.files?.[0]
          if (file) onPick(file)
          event.target.value = ''
        }}
      />

      {preview ? (
        <div
          onDragOver={event => {
            event.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={drop}
        >
          {/* Same crop as the published header, so what is cut off is visible here. */}
          <button
            type="button"
            onClick={() => input.current?.click()}
            disabled={disabled}
            className={`group relative block aspect-video w-full overflow-hidden rounded border ${
              dragging ? 'border-gray-900' : 'border-gray-200'
            }`}
          >
            <img src={preview} alt="" className="h-full w-full object-cover" />
            <span className="absolute inset-0 hidden items-center justify-center bg-gray-900/55 text-xs font-medium text-white group-hover:flex">
              <ImagePlus className="mr-1.5 h-4 w-4" /> Choose a different image
            </span>
          </button>
          {image ? (
            <ul className="mt-2 space-y-0.5">
              {notesFor(image).map(note => (
                <li
                  key={note.text}
                  className={`text-xs ${note.warn ? 'text-amber-700' : 'text-gray-500'}`}
                >
                  {note.text}
                </li>
              ))}
              <li className="text-xs text-gray-400">Uploads when you publish.</li>
            </ul>
          ) : (
            <p className="mt-2 text-xs text-gray-500">
              <code className="font-mono">{filename}</code> — already in the repository.
            </p>
          )}
        </div>
      ) : (
        <div
          onDragOver={event => {
            event.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={drop}
          className={`flex aspect-video w-full flex-col items-center justify-center gap-2 rounded border-2 border-dashed px-4 text-center ${
            dragging ? 'border-gray-900 bg-gray-50' : 'border-gray-200'
          }`}
        >
          {status === 'loading' ? (
            <p className="flex items-center text-sm text-gray-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading the current cover…
            </p>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => input.current?.click()} disabled={disabled}>
                <ImagePlus className="mr-1.5 h-4 w-4" /> Upload cover
              </Button>
              <p className="text-xs text-gray-500">
                or drop an image here · 16:9, at least {COVER_WIDTH_PX}px wide
              </p>
              <p className="text-xs text-gray-400">
                Optional. Without one the site draws its own orange cover with the post title.
              </p>
            </>
          )}
        </div>
      )}

      {status === 'missing' && (
        <div className="mt-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2.5 text-xs text-red-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            This post names <code className="font-mono">{filename}</code> as its cover, but that file
            is not in the repository. Publishing it would break the website build, so upload a cover
            or remove it.
          </span>
        </div>
      )}

      {/* Only once there is a cover to describe: the generated fallback carries
          the title as real text and needs no alt at all. */}
      {has && (
        <div className="mt-3">
          <label
            htmlFor="cover-alt"
            className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-500"
          >
            Alt text
          </label>
          <input
            id="cover-alt"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
            value={alt}
            disabled={disabled}
            placeholder="Describe what the cover shows"
            onChange={event => onAlt(event.target.value)}
          />
          <p className={`mt-1 text-xs ${alt.trim() ? 'text-gray-400' : 'text-amber-700'}`}>
            {alt.trim()
              ? 'Read out in place of the image, and used by search engines.'
              : 'Without this the cover is announced as nothing. Leave it empty only if the cover is purely decorative.'}
          </p>
        </div>
      )}
    </div>
  )
}
